import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Box, OutlinedInput, Paper, Stack, Typography } from '@mui/material'
import React from 'react'
import { i18n } from '../../index/index'
import { ShiftPercentages } from './types'

export interface ShiftWeightsPanelProps {
  shiftPercentages: ShiftPercentages
  isEditing: boolean
  onChange: (shift: keyof ShiftPercentages, value: number) => void
  /** 'sidebar' = borderless column flush to the table with a left divider (desktop
   * side-by-side); 'card' = standalone bordered card (stacked tablet-portrait / mobile). */
  variant?: 'sidebar' | 'card'
}

const SHIFT_ORDER: Array<{ key: keyof ShiftPercentages; labelKey: string }> = [
  { key: 'Morning', labelKey: 'morningShift' },
  { key: 'Evening', labelKey: 'eveningShift' },
  { key: 'Night', labelKey: 'nightShift' },
]

/**
 * Shift weighting side panel (Figma): title + subtitle, one row per shift (each with a bottom
 * divider) with the shift label on the left and a % input/value on the right, and a
 * "Yhteensä <sum> %" row on a grey background that turns green at exactly 100 %.
 *
 * Layout matches the table rows: a header area with a bottom divider, then evenly-divided
 * rows. In 'sidebar' (desktop) it is a borderless column with a left divider so it reads as
 * one card with the table; in 'card' (tablet/mobile) it is a standalone bordered Paper.
 */
export function ShiftWeightsPanel({ shiftPercentages, isEditing, onChange, variant = 'card' }: ShiftWeightsPanelProps) {
  const total = SHIFT_ORDER.reduce((sum, s) => sum + (Number(shiftPercentages[s.key]) || 0), 0)
  const roundedTotal = Math.round(total * 10) / 10
  const isHundred = Math.abs(roundedTotal - 100) < 0.5

  const rowSx = { borderBottom: 1, borderColor: 'divider' }

  const content = (
    <>
      {/* Header — aligns with the table's column-header row */}
      <Box sx={{ px: 2, py: 1.5, ...rowSx }}>
        <Typography variant='subtitle1' fontWeight={600}>
          {i18n.t('admin.monthlyTargets.shiftWeightsPanelTitle')}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {i18n.t('admin.monthlyTargets.shiftWeightsPanelSubtitle')}
        </Typography>
      </Box>

      {SHIFT_ORDER.map((shift) => (
        <Box
          key={shift.key}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            minHeight: 56,
            ...rowSx,
          }}
        >
          <Typography variant='body2'>{i18n.t(`admin.monthlyTargets.${shift.labelKey}`)}</Typography>
          {isEditing ? (
            <OutlinedInput
              type='number'
              size='small'
              value={shiftPercentages[shift.key] || ''}
              onChange={(event) => onChange(shift.key, Number(event.target.value))}
              endAdornment={<Typography variant='body2'>%</Typography>}
              sx={{ width: 100 }}
              inputProps={{ style: { textAlign: 'right' } }}
            />
          ) : (
            <Typography variant='body2' fontWeight={600}>
              {shiftPercentages[shift.key]} %
            </Typography>
          )}
        </Box>
      ))}

      {/* Yhteensä total — grey background band */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          minHeight: 48,
          backgroundColor: 'grey.50',
          ...rowSx,
        }}
      >
        <Typography variant='body2'>{i18n.t('admin.monthlyTargets.shiftWeightsTotal')}</Typography>
        <Stack direction='row' spacing={0.5} alignItems='center'>
          {isHundred ? (
            <CheckCircleOutlineIcon fontSize='small' color='success' />
          ) : (
            <ErrorOutlineIcon fontSize='small' color='warning' />
          )}
          <Typography variant='body2' color={isHundred ? 'success.main' : 'warning.main'}>
            {roundedTotal} %
          </Typography>
        </Stack>
      </Box>
    </>
  )

  if (variant === 'sidebar') {
    // Flush column beside the table, separated only by a left divider, so the table + panel
    // read as one card (desktop Figma).
    return <Box sx={{ borderLeft: 1, borderColor: 'divider', height: '100%' }}>{content}</Box>
  }

  return <Paper variant='outlined'>{content}</Paper>
}
